// src/pages/user/UserLearnProjects.tsx
// Route: /user/learn/:childId/projects
//
// "My projects": everything the child has made in the in-app workspaces
// (Scratch, and the code editor), to carry on building outside a lesson,
// download, rename or tidy up. Inside a lesson the Do step opens the right
// project by itself; this page is for everything else.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Atom, Blocks, Code2, Plus, Smartphone, Trash2 } from "lucide-react";
import { selectRole } from "../../app/features/authSlice";
import { useChildMode } from "../../app/childMode";
import { errorText, useGetJourneyQuery } from "../../app/api/learningApi";
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectsQuery,
} from "../../app/api/workspaceApi";
import type { WorkspaceKind } from "../../app/api/workspaceApi";
import { WorkspaceOverlay } from "../../components/workspace";
import { fmtAgo } from "../../components/workspace/LessonWorkspace";
const fmtBytes = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

const KINDS_FOR_TRACK: Record<string, WorkspaceKind[]> = {
  foundation_builders: ["scratch", "web"],
  explorers: ["web"],
  innovators: ["web", "react", "native"],
};

const KIND = {
  scratch: { label: "Scratch", icon: Blocks, newLabel: "New Scratch project" },
  web: { label: "Website (HTML, CSS, JavaScript)", icon: Code2, newLabel: "New website" },
  react: { label: "React app", icon: Atom, newLabel: "New React app" },
  native: { label: "React Native app", icon: Smartphone, newLabel: "New React Native app" },
} as const;

export default function UserLearnProjects() {
  const { childId = "" } = useParams();
  const role = useSelector(selectRole);
  const childMode = useChildMode();
  const isParent = role === "parent" && !childMode;
  const { data: journey } = useGetJourneyQuery(childId);
  const { data, isLoading, error } = useGetProjectsQuery({ childId });
  const [create, { isLoading: creating }] = useCreateProjectMutation();
  const [remove] = useDeleteProjectMutation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const child = journey?.data.child;
  const kinds = KINDS_FOR_TRACK[child?.track ?? ""] ?? ["web"];
  const projects = data?.data ?? [];

  const startNew = async (kind: WorkspaceKind) => {
    setMsg(null);
    try {
      const { data: p } = await create({ childId, kind }).unwrap();
      setOpenId(p.id);
    } catch (err) {
      setMsg(errorText(err, "Could not start a new project"));
    }
  };

  const onDelete = async (id: string, name: string) => {
    if (!window.confirm(`Remove “${name}”? This can't be undone. (Anything already handed in stays with its lesson.)`)) return;
    try {
      await remove({ childId, id }).unwrap();
    } catch (err) {
      setMsg(errorText(err, "Could not remove it"));
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to={`/user/learn/${childId}`} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5f6780] hover:text-[#16213e]">
        <ArrowLeft className="h-4 w-4" /> Back to the lessons
      </Link>

      <div>
        <h1 className="text-[1.75rem] font-extrabold tracking-[-0.03em] text-[#16213e]">
          {isParent && child ? `${child.firstName}'s projects` : "My projects"}
        </h1>
        <p className="mt-0.5 text-[15px] text-[#5f6780]">
          {kinds.includes("scratch")
            ? "Everything made in the lessons' Scratch and code editors. It saves by itself."
            : kinds.includes("native")
              ? "Websites, React apps and React Native apps made in the code editor. They save by themselves; download one to carry on in VS Code or Expo."
              : "Everything made in the lessons' code editor. It saves by itself."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {kinds.map((k) => {
          const Icon = KIND[k].icon;
          return (
            <button
              key={k}
              type="button"
              onClick={() => startNew(k)}
              disabled={creating}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#24397a] disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> <Icon className="h-4 w-4" /> {KIND[k].newLabel}
            </button>
          );
        })}
      </div>
      {msg && <p className="text-sm font-semibold text-[#b94a3a]">{msg}</p>}

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-[24px] bg-[#ebeff7]" />
      ) : error ? (
        <p className="rounded-[24px] border border-[#f5d9a0] bg-[#fff8ee] p-6 text-sm text-[#7a4a10]">{errorText(error, "Could not load the projects")}</p>
      ) : projects.length === 0 ? (
        <p className="rounded-[24px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-8 text-center text-sm text-[#5f6780]">
          No projects yet. They appear here once {isParent && child ? child.firstName : "you"} start{isParent ? "s" : ""} one in a lesson, or with the buttons above.
        </p>
      ) : (
        <ul className="divide-y divide-[#ebeff7] rounded-[24px] bg-white ring-1 ring-[#e5e8ef]">
          {projects.map((p) => {
            const Icon = KIND[p.kind].icon;
            return (
              <li key={p.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#ebeff7]">
                  <Icon className="h-5 w-5 text-[#1b2d5e]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-bold text-[#16213e]">{p.name}</span>
                  <span className="block text-[12.5px] text-[#8189a0]">
                    {KIND[p.kind].label} · saved {fmtAgo(p.updatedAt)}
                    {p.bytes ? ` · ${fmtBytes(p.bytes)}` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setOpenId(p.id)}
                  className="rounded-xl bg-[#1b2d5e] px-3.5 py-2 text-[13px] font-bold text-white hover:bg-[#24397a]"
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(p.id, p.name)}
                  className="rounded-xl p-2 text-[#b94a3a] hover:bg-[#fff5f3]"
                  aria-label={`Remove ${p.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {openId && <WorkspaceOverlay childId={childId} projectId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
